import { useState, useEffect, useCallback } from "react";
import { createContext, useContext, useReducer } from "react";

import AlertList from "./AlertList.js";
import Wait from "./Wait.js";
import ConnectionLost from "./ConnectionLost.js";

const VERBOSE = false;
const REFRESH_TIME = 1000;
const LOCAL = window.location.hostname === "localhost";

export const PHP_PATH = LOCAL ? "http://localhost/simapp/src/php/" : "./php/";
export const API_PATH = LOCAL ? "http://localhost:3000/" : "https://legamelab.com/simapp/";

const dataInit = {
    rememberMe: null,
    token: null,
    teacherID: null,
    simID: null,
    groupID: null,
    tabName: null,
    amendmentState: null,
    editAmendment: null,
    browseAmendment: false,
    admin: false,
    alert: {},
    teacher: {},
    sim: {},
    institution: {},
    scenario: {},
    duration: {},
    group: {},
    student: {},
    article: {},
    course: {},
    amendment: {},
    vote: {},
    institutionContent: {},
    scenarioContent: {},
    durationContent: {},
    groupContent: {},
    articleContent: {},
    courseContent: {},
};

let refreshInterval;

const localFields = ["rememberMe", "teacherID", "simID", "tabName", "amendmentState", "token"];

const setLocal = (field, value) => {
    localStorage.setItem(field, JSON.stringify(value));
};

const getLocal = (field) => {
    try { return JSON.parse(localStorage.getItem(field)); }
    catch (error) { return null; }
};


function dataReducer (data, action)
{
    var newData = { ...data };
    Object.keys(action.data).forEach(dataKey => {
	if (dataKey === "success" || dataKey === "datetime") return;
	// TODO: below, find a way to descriminate between "local data" and "global data"
	if (dataKey !== "amendmentState" && typeof newData[dataKey] === "object" && newData[dataKey] !== null && action.data[dataKey] !== null)
	{
	    Object.entries(action.data[dataKey]).forEach(([key, value]) => {
		if (value === null) delete newData[dataKey][key];
		else newData[dataKey][key] = value;
	    });
	}
	else { newData[dataKey] = action.data[dataKey]; }
    });

    if (newData.rememberMe !== null && newData.rememberMe)
    {
	localFields.forEach((field) => { setLocal(field, newData[field]); });
    }
    
    return newData;
    //newData.alerts = { ...newData.alerts, ...action.data.alerts };
    //return { ...data, ...action.data };
}

const DataContext = createContext(null);

const DispatchContext = createContext(null);

export function DataProvider ({ children })
{
    const [loading, setLoading] = useState(false);
    const [connectionLost, setConnectionLost] = useState(false);
    const [data, dispatch] = useReducer(dataReducer, dataInit);

    const asyncDispatch = useCallback((action) => {
	if (VERBOSE && action.type !== "update") console.log("START DISPATCH " + action.type);
	switch (action.type)
	{
	    case "teacherSignin" :
	    {
		const callback = () => { asyncDispatch({ type: "alert", data: { variant: "success", title: "Nouveau compte", content: "Votre compte SIM'App a été créé avec succès. Vous pouvez maintenant vous connecter." }}); };
		api("signin.php", { data: action.data }, true, callback);
	    }
	    break;

	    case "teacherLogin" :
	    {
		const sentData = { data: { ...action.data, user: "teacher" } };
		const callback = (receivedData) => {
		    if (receivedData.token === null)
			asyncDispatch({ type: "alert", data: { variant: "danger", title: "Erreur d'identification", content: "Adresse inconnue ou mot de passe erroné." }});
		};
		dispatch({ data: { rememberMe: action.data.rememberMe } });
		api("login.php", sentData, true, callback);
	    }
	    break;
	    
	    case "teacherLogout" :
	    dispatch({ data: dataInit });
	    localStorage.clear();
	    break;

	    case "groupLogin" :
	    {
		const sentData = { data: { ...action.data, user: "group" } };
		const callback = (receivedData) => {
		    if (receivedData.token === null)
			asyncDispatch({ type: "alert", data: { variant: "danger", title: "Erreur d'identification", content: "Code SIM inconnu. Veuillez vérifier auprès de votre enseignant·e que vous avez le bon code d'identification." }});
		};
		api("login.php", sentData, true, callback);
	    }
	    break;
	    
	    case "continue" :
	    {
		const data = Object.fromEntries(localFields.map((field) => { return [field, getLocal(field)]; }));
		dispatch({ data });
	    }
	    break;

	    case "newSim" :
	    {
		const sentData = { token: data.token, data: action.data };
		const tables = ["sim", "institution", "scenario", "duration", "group", "student", "article", "course"];
		api("newSim.php", sentData, true, () => { asyncDispatch({ type: "update", tables }); }); // TODO : specify update
	    }
	    break;

	    case "deleteSim" :
	    {
		const sentData = { token: data.token, simID: action.simID };
		/* const tables = ["sim", "institution", "scenario", "duration", "group", "student", "article", "course"]; */
		api("deleteSim.php", sentData); //, () => { asyncDispatch({ type: "update", tables }); });
	    }
	    break;

	    case "openSim" : dispatch({ data: { simID: action.simID, tabName: "sim" }}); break;
	    case "openTab" : dispatch({ data: { tabName: action.tabName }}); break;
	    case "closeSim" : dispatch({ data: { simID: null }}); break;

	    case "setAmendmentState" : dispatch({ data: { amendmentState: { state: action.state, displayAll: action.displayAll, articleID: action.articleID, amendmentID: action.amendmentID } }}); break;
	    case "unsetAmendmentState" : dispatch({ data: { amendmentState: null }}); break;

	    case "showEditAmendment" :
	    if (action.amendmentID !== undefined) dispatch({ data: { editAmendment: { amendmentID: action.amendmentID } } });
	    else dispatch({ data: { editAmendment: { amendmentID: null, articleID: action.articleID, prevArticleID: action.prevArticleID, sessionNum: action.sessionNum } } });
	    break;

	    case "hideEditAmendment" : dispatch({ data: { editAmendment: null } }); break;

	    case "showBrowseAmendment" : dispatch({ data: { browseAmendment: true } }); break;
	    case "hideBrowseAmendment" : dispatch({ data: { browseAmendment: false } }); break;

	    case "newArticle" :
	    {
		const sentData = { token: data.token, data: action.data };
		api("newArticle.php", sentData, true); // TODO : specify update
	    }
	    break;

	    case "newSession" :
	    {
		const sentData = { token: data.token };
		api("newSession.php", sentData, true);
	    }
	    break;

	    case "upload" :
	    {
		//TODO: check why teacherID is also updated
		const sentData = { ...action, token: data.token };
		if (data.teacherID !== null)
		{
		    sentData.user = "teacher";
		    api("setData.php", sentData, true, () => { asyncDispatch({ type: "update", tables: [action.table] }); }); // TODO : specify update
		}

		else if (data.groupID !== null)
		{
		    sentData.user = "group";
		    api("setData.php", sentData, true); // TODO : specify update
		}
	    }
	    break;

	    case "update" :
	    {
		const sentData = { token: data.token, tables: action.tables, datetime: action.datetime === undefined ? null : action.datetime };
		if (data.teacherID !== null) sentData.user = "teacher";
		else if (data.groupID !== null) sentData.user = "group";
		api("getData.php", sentData, action.wait === undefined || action.wait, action.callback === undefined ? () => {} : action.callback);
	    }
	    break;

	    case "delete" :
	    {
		const sentData = { ...action, token: data.token };
		api("deleteData.php", sentData, true, () => { asyncDispatch({ type: "update", tables: [action.table] }); }); // TODO : specify update
	    }
	    break;

	    case "alert" :
	    {
		const alertID = Object.keys(data.alert).length;
		action.data.alertID = alertID;
		dispatch({ data: { alert: { [alertID]: action.data }}});
	    }
	    break;

	    default: console.error("unknown dispatch action: " + action.type);
	}
    }, [data.alert, data.token, data.groupID, data.teacherID]);

    useEffect(() => {
	//console.log({...data});
	if (VERBOSE)
	{
	    console.log("NEW DATA");
	    console.log(JSON.parse(JSON.stringify(data)));
	}
    }, [data]);

    useEffect(() => {
	if (data.token !== null)
	{
	    const tables = ["sim", "institution", "scenario", "duration", "group", "student", "article", "course", "amendment", "vote"];
	    if (data.teacherID !== null) tables.push("teacher", "institutionContent", "scenarioContent", "durationContent");

	    let datetime = null;
	    asyncDispatch({ type: "update", tables, wait: true, datetime, callback: (receivedData) => { datetime = receivedData.datetime; } });

	    //if (data.teacherID === null) // TODO : better update if SIM is not running?
	    refreshInterval = setInterval(() => {
		asyncDispatch({ type: "update", tables, wait: false, datetime, callback: (receivedData) => { datetime = receivedData.datetime; } });
	    }, REFRESH_TIME);
	    //return () => clearInterval(refreshInterval);
	}
	else clearInterval(refreshInterval); // TODO: remove data
    }, [data.token, data.teacherID, asyncDispatch]);

    useEffect(() => {
	const admin = data.teacherID !== null || (data.groupID !== null && data.group[data.groupID] !== undefined && data.group[data.groupID].type === "admin");
	dispatch({ data: { admin }});
    }, [data.teacherID, data.group, data.groupID, data.group[data.groupID]]);
    
    async function api (scriptFile, sentData, wait = true, callback = () => {})
    {
	if (wait) setLoading(true);
	const requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(sentData),
	};

	try {
	    const response = await fetch(PHP_PATH + scriptFile, requestOptions);
	    const receivedData = await response.json();
	    if (JSON.stringify(Object.keys(receivedData)) !== JSON.stringify(["success", "datetime"])) dispatch({ data: receivedData }); // TODO : check recomputation if specific data changes
	    if (wait) setLoading(false);
	    setConnectionLost(false);
	    callback(receivedData);
	}

	catch (error)
	{
	    console.log(error);
	    setConnectionLost(true);
	    if (wait) setLoading(false);
	}
    }
    
    return (
	<DataContext.Provider value={data}>
	    <DispatchContext.Provider value={asyncDispatch}>
		<ConnectionLost show={connectionLost}/>
		<AlertList/>
		{loading && <Wait/>}
		{children}
	    </DispatchContext.Provider>
	</DataContext.Provider>
    );
    // Note: memoize the context value, if Provider gets re-rendered more often
}

export function useData ()
{
    return useContext(DataContext);
}

export function useDispatch ()
{
    return useContext(DispatchContext);
}

